import React, { useState, useEffect, useRef, useMemo } from 'react';
import AppLayout from '../components/AppLayout';
import '../hd-styles.css';

const API_URL = '/api';

function Network() {
  const [networkStats, setNetworkStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsHistory, setStatsHistory] = useState([]);
  const statsHistoryRef = useRef([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [pulsePhase, setPulsePhase] = useState(0);

  useEffect(() => {
    loadNetworkData();
    const interval = setInterval(loadNetworkData, 3000);
    return () => clearInterval(interval);
  }, []);

  // Neural pulse animation
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setPulsePhase(p => (p + 1) % 100);
    }, 50);
    return () => clearInterval(pulseInterval);
  }, []);

  // Generate stable positions for neural network layout
  const nodePositions = useMemo(() => {
    const positions = [];
    const width = 900;
    const height = 500;
    const padding = 60;

    // Use seeded random for consistent positions
    const seededRandom = (seed) => {
      const x = Math.sin(seed * 9999) * 10000;
      return x - Math.floor(x);
    };

    for (let i = 0; i < 50; i++) {
      // Create organic clustered positions
      const angle = seededRandom(i * 13) * Math.PI * 2;
      const radius = seededRandom(i * 17) * (Math.min(width, height) / 2 - padding);
      const clusterX = width / 2 + Math.cos(angle) * radius * 0.7;
      const clusterY = height / 2 + Math.sin(angle) * radius * 0.8;

      // Add some jitter
      const jitterX = (seededRandom(i * 23) - 0.5) * 80;
      const jitterY = (seededRandom(i * 29) - 0.5) * 80;

      positions.push({
        x: Math.max(padding, Math.min(width - padding, clusterX + jitterX)),
        y: Math.max(padding, Math.min(height - padding, clusterY + jitterY))
      });
    }
    return positions;
  }, []);

  // Calculate connections between nearby nodes
  const connections = useMemo(() => {
    const conns = [];
    const maxDistance = 150;

    for (let i = 0; i < nodePositions.length; i++) {
      for (let j = i + 1; j < nodePositions.length; j++) {
        const dx = nodePositions[i].x - nodePositions[j].x;
        const dy = nodePositions[i].y - nodePositions[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < maxDistance) {
          conns.push({
            from: i,
            to: j,
            distance,
            opacity: 1 - (distance / maxDistance)
          });
        }
      }
    }
    return conns;
  }, [nodePositions]);

  const loadNetworkData = async () => {
    try {
      const [statsRes, workersRes] = await Promise.all([
        fetch(`${API_URL}/stats`),
        fetch(`${API_URL}/leaderboard?limit=50`)
      ]);

      const statsData = await statsRes.json();
      const workersData = await workersRes.json();

      // Map API response to expected format
      const mappedStats = {
        online_workers: statsData.network?.workers_online || statsData.active_workers || 0,
        total_cores: statsData.network?.total_cores || 0,
        total_cache_hits: statsData.lifetime?.cache_served || statsData.lifetime?.queries_served || statsData.network?.total_cache_served || 0,
        co2_saved_kg: statsData.co2_impact?.saved_total_kg || 0
      };
      setNetworkStats(mappedStats);

      // Update history for chart
      const newPoint = {
        time: Date.now(),
        workers: mappedStats.online_workers,
        cores: mappedStats.total_cores,
        queries: mappedStats.total_cache_hits
      };
      statsHistoryRef.current = [...statsHistoryRef.current.slice(-59), newPoint];
      setStatsHistory([...statsHistoryRef.current]);

      if (workersData.success) {
        setWorkers(workersData.leaderboard || []);
      }
    } catch (err) {
      console.error('Network data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNum = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return Math.round(num).toLocaleString();
  };

  // Real-time line chart
  const LiveChart = ({ data, dataKey, color, label, height = 100 }) => {
    const width = 600;
    const padding = { top: 20, right: 20, bottom: 30, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const values = data.map(d => d[dataKey]);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);

    const points = data.map((d, i) => {
      const x = padding.left + (i / Math.max(data.length - 1, 1)) * chartWidth;
      const y = padding.top + chartHeight - ((d[dataKey] - min) / (max - min || 1)) * chartHeight;
      return { x, y, value: d[dataKey] };
    });

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    return (
      <div className="live-chart-container">
        <div className="live-chart-header">
          <span className="live-chart-label">{label}</span>
          <span className="live-chart-value" style={{ color }}>{formatNum(values[values.length - 1] || 0)}</span>
        </div>
        <svg viewBox={`0 0 ${width} ${height}`} className="live-chart">
          <defs>
            <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
              <stop offset="100%" stopColor={color} stopOpacity="0"/>
            </linearGradient>
          </defs>

          {/* Grid */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
            <line
              key={i}
              x1={padding.left}
              y1={padding.top + chartHeight * ratio}
              x2={width - padding.right}
              y2={padding.top + chartHeight * ratio}
              stroke="#e5e7eb"
              strokeDasharray="4 4"
            />
          ))}

          {/* Area */}
          {points.length > 1 && (
            <path
              d={`${linePath} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`}
              fill={`url(#grad-${dataKey})`}
            />
          )}

          {/* Line */}
          {points.length > 1 && (
            <path
              d={linePath}
              fill="none"
              stroke={color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Current point with pulse */}
          {points.length > 0 && (
            <g>
              <circle
                cx={points[points.length - 1].x}
                cy={points[points.length - 1].y}
                r="8"
                fill={color}
                fillOpacity="0.2"
                className="pulse-ring"
              />
              <circle
                cx={points[points.length - 1].x}
                cy={points[points.length - 1].y}
                r="4"
                fill={color}
              />
            </g>
          )}

          {/* Y axis labels */}
          <text x={padding.left - 5} y={padding.top + 5} textAnchor="end" className="chart-axis-label">{formatNum(max)}</text>
          <text x={padding.left - 5} y={padding.top + chartHeight} textAnchor="end" className="chart-axis-label">{formatNum(min)}</text>

          {/* X axis label */}
          <text x={width - padding.right} y={height - 5} textAnchor="end" className="chart-axis-label">Now</text>
          <text x={padding.left} y={height - 5} textAnchor="start" className="chart-axis-label">-3 min</text>
        </svg>
      </div>
    );
  };

  // Neural Node component
  const NeuralNode = ({ worker, position, index, onClick, isSelected, isOnline, pulsePhase }) => {
    const size = isOnline ? 28 : 20;
    const baseColor = isOnline ? '#10b981' : '#475569';

    // Pulse effect for online nodes
    const pulseScale = isOnline ? 1 + Math.sin((pulsePhase + index * 10) * 0.1) * 0.15 : 1;
    const glowRadius = isOnline ? size + 15 + Math.sin((pulsePhase + index * 5) * 0.08) * 8 : 0;

    return (
      <g
        onClick={() => onClick(worker)}
        style={{ cursor: 'pointer' }}
        className={`neural-node ${isSelected ? 'selected' : ''} ${isOnline ? 'online' : 'offline'}`}
      >
        {/* Outer glow for online nodes */}
        {isOnline && (
          <>
            <circle
              cx={position.x}
              cy={position.y}
              r={glowRadius}
              fill="url(#neuralGlow)"
              className="neural-glow"
            />
            <circle
              cx={position.x}
              cy={position.y}
              r={size + 8}
              fill={baseColor}
              fillOpacity={0.2 + Math.sin((pulsePhase + index * 7) * 0.1) * 0.1}
            />
          </>
        )}

        {/* Main node */}
        <circle
          cx={position.x}
          cy={position.y}
          r={size * pulseScale / 2}
          fill={baseColor}
          stroke={isSelected ? '#3b82f6' : isOnline ? '#34d399' : '#64748b'}
          strokeWidth={isSelected ? 3 : isOnline ? 2 : 1}
          fillOpacity={isOnline ? 1 : 0.4}
        />

        {/* Inner highlight */}
        {isOnline && (
          <circle
            cx={position.x - size * 0.15}
            cy={position.y - size * 0.15}
            r={size * 0.2}
            fill="white"
            fillOpacity={0.3}
          />
        )}

        {/* Node label */}
        <text
          x={position.x}
          y={position.y + 4}
          textAnchor="middle"
          fill="white"
          fontSize={isOnline ? "11" : "9"}
          fontWeight="600"
          style={{ pointerEvents: 'none' }}
        >
          {(worker?.display_name || 'A')[0]}
        </text>
      </g>
    );
  };

  // Synapse/Connection with traveling pulse
  const Synapse = ({ from, to, opacity, pulsePhase, index, isActive }) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    // Calculate pulse position along the connection
    const pulseProgress = ((pulsePhase + index * 15) % 100) / 100;
    const pulseX = from.x + dx * pulseProgress;
    const pulseY = from.y + dy * pulseProgress;

    return (
      <g className="synapse">
        {/* Base connection line */}
        <line
          x1={from.x}
          y1={from.y}
          x2={to.x}
          y2={to.y}
          stroke={isActive ? '#10b981' : '#64748b'}
          strokeWidth={isActive ? 1.5 : 0.5}
          strokeOpacity={isActive ? opacity * 0.6 : opacity * 0.15}
        />

        {/* Traveling pulse for active connections */}
        {isActive && (
          <>
            <circle
              cx={pulseX}
              cy={pulseY}
              r={3}
              fill="#10b981"
              fillOpacity={0.8}
              className="traveling-pulse"
            />
            <circle
              cx={pulseX}
              cy={pulseY}
              r={6}
              fill="#10b981"
              fillOpacity={0.3}
            />
          </>
        )}
      </g>
    );
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Connecting to network...</p>
        </div>
      </AppLayout>
    );
  }

  const onlineWorkers = workers.filter(w => w.worker_status === 'online');
  const offlineWorkers = workers.filter(w => w.worker_status !== 'online');

  return (
    <AppLayout>
      <div className="page-content network-page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left">
            <h1>Network Live</h1>
            <p className="page-subtitle">Real-time view of the AVALW distributed network</p>
          </div>
          <div className="network-live-indicator">
            <span className="live-pulse"></span>
            <span>Live</span>
            <span className="refresh-rate">3s refresh</span>
          </div>
        </div>

        {/* Big Stats */}
        <div className="network-big-stats">
          <div className="big-stat green">
            <div className="big-stat-icon">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
            </div>
            <div className="big-stat-content">
              <span className="big-stat-value">{networkStats?.online_workers || 0}</span>
              <span className="big-stat-label">Contributors Online</span>
            </div>
            <div className="big-stat-trend">
              <span className="trend-icon up">+</span>
            </div>
          </div>

          <div className="big-stat blue">
            <div className="big-stat-icon">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/></svg>
            </div>
            <div className="big-stat-content">
              <span className="big-stat-value">{networkStats?.total_cores || 0}</span>
              <span className="big-stat-label">Total CPU Cores</span>
            </div>
          </div>

          <div className="big-stat purple">
            <div className="big-stat-icon">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
            </div>
            <div className="big-stat-content">
              <span className="big-stat-value">{formatNum(networkStats?.total_cache_hits || 0)}</span>
              <span className="big-stat-label">Total Queries</span>
            </div>
          </div>

          <div className="big-stat orange">
            <div className="big-stat-icon">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
            </div>
            <div className="big-stat-content">
              <span className="big-stat-value">{(networkStats?.co2_saved_kg || 0).toFixed(1)}</span>
              <span className="big-stat-label">kg CO2 Saved</span>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="network-charts-row">
          <div className="card chart-card">
            <LiveChart
              data={statsHistory}
              dataKey="workers"
              color="#10b981"
              label="Active Contributors"
            />
          </div>
          <div className="card chart-card">
            <LiveChart
              data={statsHistory}
              dataKey="cores"
              color="#3b82f6"
              label="Network Cores"
            />
          </div>
        </div>

        {/* Neural Network Visualization */}
        <div className="card neural-network-card">
          <div className="card-header">
            <h3>Neural Activity Map</h3>
            <div className="neural-legend">
              <span className="legend-item">
                <span className="legend-dot online pulse"></span>
                Active ({onlineWorkers.length})
              </span>
              <span className="legend-item">
                <span className="legend-dot offline"></span>
                Dormant ({offlineWorkers.length})
              </span>
              <span className="neural-activity-indicator">
                <span className="activity-wave"></span>
                <span className="activity-wave"></span>
                <span className="activity-wave"></span>
                Network Active
              </span>
            </div>
          </div>

          <div className="neural-network-container">
            <svg viewBox="0 0 900 500" className="neural-network-svg">
              <defs>
                {/* Glow gradient for online nodes */}
                <radialGradient id="neuralGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                  <stop offset="70%" stopColor="#10b981" stopOpacity="0.1" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                </radialGradient>

                {/* Background grid pattern */}
                <pattern id="neuralGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="1" fill="#e2e8f0" fillOpacity="0.3" />
                </pattern>

                {/* Pulse animation filter */}
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              {/* Background */}
              <rect width="900" height="500" fill="url(#neuralGrid)" />

              {/* Synaptic connections */}
              {connections.map((conn, idx) => {
                const fromWorker = workers[conn.from];
                const toWorker = workers[conn.to];
                const isActive = fromWorker?.worker_status === 'online' && toWorker?.worker_status === 'online';

                return (
                  <Synapse
                    key={`synapse-${idx}`}
                    from={nodePositions[conn.from]}
                    to={nodePositions[conn.to]}
                    opacity={conn.opacity}
                    pulsePhase={pulsePhase}
                    index={idx}
                    isActive={isActive}
                  />
                );
              })}

              {/* Neural nodes */}
              {workers.slice(0, 50).map((worker, index) => (
                <NeuralNode
                  key={worker.id}
                  worker={worker}
                  position={nodePositions[index]}
                  index={index}
                  onClick={setSelectedWorker}
                  isSelected={selectedWorker?.id === worker.id}
                  isOnline={worker.worker_status === 'online'}
                  pulsePhase={pulsePhase}
                />
              ))}

              {/* Central hub indicator */}
              <g className="central-hub">
                <circle
                  cx="450"
                  cy="250"
                  r={20 + Math.sin(pulsePhase * 0.05) * 5}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2"
                  strokeOpacity={0.3}
                  strokeDasharray="5 5"
                />
                <circle
                  cx="450"
                  cy="250"
                  r={35 + Math.sin(pulsePhase * 0.03) * 8}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="1"
                  strokeOpacity={0.15}
                  strokeDasharray="3 3"
                />
              </g>
            </svg>

            {/* Activity overlay stats */}
            <div className="neural-overlay-stats">
              <div className="nos-item">
                <span className="nos-value">{((onlineWorkers.length / Math.max(workers.length, 1)) * 100).toFixed(0)}%</span>
                <span className="nos-label">Network Health</span>
              </div>
              <div className="nos-item">
                <span className="nos-value">{connections.filter(c => {
                  const fw = workers[c.from];
                  const tw = workers[c.to];
                  return fw?.worker_status === 'online' && tw?.worker_status === 'online';
                }).length}</span>
                <span className="nos-label">Active Synapses</span>
              </div>
            </div>
          </div>

          {/* Selected worker info */}
          {selectedWorker && (
            <div className="selected-worker-info">
              <div className="swi-header">
                <div className="swi-avatar" style={{ backgroundColor: selectedWorker.badge?.color || '#6366f1' }}>
                  {(selectedWorker.display_name || 'A')[0]}
                </div>
                <div className="swi-details">
                  <h4>{selectedWorker.display_name || 'Anonymous'}</h4>
                  <span className={`status-badge ${selectedWorker.worker_status}`}>
                    {selectedWorker.worker_status === 'online' ? 'Online' : 'Offline'}
                  </span>
                </div>
                <button className="swi-close" onClick={() => setSelectedWorker(null)}>×</button>
              </div>
              <div className="swi-stats">
                <div className="swi-stat">
                  <span className="swi-stat-value">{formatNum(selectedWorker.core_hours_total || 0)}</span>
                  <span className="swi-stat-label">Core Hours</span>
                </div>
                <div className="swi-stat">
                  <span className="swi-stat-value">{formatNum(selectedWorker.total_cache_served || 0)}</span>
                  <span className="swi-stat-label">Cache Served</span>
                </div>
                <div className="swi-stat">
                  <span className="swi-stat-value">{selectedWorker.uptime_days || 0}d</span>
                  <span className="swi-stat-label">Uptime</span>
                </div>
                <div className="swi-stat">
                  <span className="swi-stat-value">{selectedWorker.badge?.name || 'Starter'}</span>
                  <span className="swi-stat-label">Badge</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Activity Feed */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Activity</h3>
          </div>
          <div className="activity-feed">
            {onlineWorkers.slice(0, 8).map((worker, i) => (
              <div key={worker.id} className="activity-item">
                <div className="activity-dot online"></div>
                <div className="activity-content">
                  <strong>{worker.display_name || 'Anonymous'}</strong> is contributing
                  <span className="activity-meta">{worker.core_hours_total || 0} core-hours total</span>
                </div>
                <span className="activity-time">Now</span>
              </div>
            ))}
            {onlineWorkers.length === 0 && (
              <div className="activity-empty">No active contributors at the moment</div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default Network;
